import { useState, useEffect } from 'react';
import { X, FileText, Image } from 'lucide-react';
import { Button } from '../Button';
import { Input } from '../Input';
import { LibraryDocumentUploader } from '../Documents/LibraryDocumentUploader';
import { DocumentPreview } from '../Documents/DocumentPreview';
import { api, ExpenseCategory, FamilyMember, type LibraryDocument } from '@/api/client';

interface AddExpenseProps {
  onCancel: () => void;
  onSave: (data: any, documentIds: string[]) => void;
  onSaveAsDraft: (data: any, documentIds: string[]) => void;
  validationErrors?: Record<string, string>;
}

export function AddExpense({ onCancel, onSave, onSaveAsDraft, validationErrors = {} }: AddExpenseProps) {
  const [formData, setFormData] = useState({
    provider: '',
    description: '',
    amount: '',
    date: '',
    expenseCategoryId: '',
    familyMemberId: '',
    notes: '',
  });

  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);

  // Document state
  const [uploadedDocs, setUploadedDocs] = useState<LibraryDocument[]>([]);
  const [previewDoc, setPreviewDoc] = useState<LibraryDocument | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await api.getExpenseCategories();
        if (data) setCategories(data);
      } catch (err) {
        console.error('Failed to load categories:', err);
      }
    };
    const loadFamilyMembers = async () => {
      try {
        const data = await api.getFamilyMembers();
        if (data) setFamilyMembers(data);
      } catch (err) {
        console.error('Failed to load family members:', err);
      }
    };
    loadCategories();
    loadFamilyMembers();
  }, []);

  // Group categories by their group field
  const groupedCategories = categories.reduce<Record<string, ExpenseCategory[]>>((acc, cat) => {
    const group = cat.group || 'Other';
    if (!acc[group]) acc[group] = [];
    acc[group].push(cat);
    return acc;
  }, {});

  const groupLabels: Record<string, string> = {
    MedicalCare: 'Medical Care',
    Medications: 'Medications',
    DentalVision: 'Dental & Vision',
    SuppliesEquipment: 'Supplies & Equipment',
    InsurancePremiums: 'Insurance Premiums',
    TransportationLodging: 'Transportation & Lodging',
    Other: 'Other',
  };

  const handleUploadComplete = (doc: LibraryDocument) => {
    setUploadedDocs(prev => [doc, ...prev]);
  };

  const handleRemoveFromStaged = (docId: string) => {
    setUploadedDocs(prev => prev.filter(d => d.id !== docId));
    if (previewDoc?.id === docId) setPreviewDoc(null);
  };

  const documentIds = uploadedDocs.map(d => d.id);

  return (
    <div className="p-8">
      <div className="max-w-[1100px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-[28px] font-semibold text-[#1B365D] mb-1">Add Expense</h1>
            <p className="text-[14px] text-[#718096]">Enter expense details and upload documents</p>
          </div>
          <button onClick={onCancel} className="text-[#718096] hover:text-[#4A5568]">
            <X size={24} />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-6">
          {/* Form Section */}
          <div className="space-y-6">
            {validationErrors.general && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-[14px] text-red-800">{validationErrors.general}</p>
              </div>
            )}

            <div className="bg-white rounded-lg border border-[#E2E8F0] p-6">
              <h2 className="text-[16px] font-semibold text-[#1B365D] mb-4">Expense Details</h2>

              <div className="space-y-4">
                <div>
                  <Input
                    label="Provider *"
                    value={formData.provider}
                    onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                    placeholder="e.g., CVS Pharmacy"
                  />
                  {validationErrors.provider && (
                    <p className="text-[12px] text-red-600 mt-1">{validationErrors.provider}</p>
                  )}
                </div>

                <div>
                  <Input
                    label="Description *"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="e.g., Annual physical exam"
                  />
                  {validationErrors.description && (
                    <p className="text-[12px] text-red-600 mt-1">{validationErrors.description}</p>
                  )}
                </div>

                <div>
                  <Input
                    label="Amount *"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0.00"
                    type="number"
                  />
                  {validationErrors.amount && (
                    <p className="text-[12px] text-red-600 mt-1">{validationErrors.amount}</p>
                  )}
                </div>

                <div>
                  <Input
                    label="Service Date *"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    type="date"
                  />
                  {validationErrors.date && (
                    <p className="text-[12px] text-red-600 mt-1">{validationErrors.date}</p>
                  )}
                </div>

                <div>
                  <label className="block text-[13px] font-semibold text-[#4A5568] mb-1.5">
                    Category
                  </label>
                  <select
                    value={formData.expenseCategoryId}
                    onChange={(e) => setFormData({ ...formData, expenseCategoryId: e.target.value })}
                    className="w-full h-11 px-3 text-[14px] border border-[#E2E8F0] rounded focus:outline-none focus:border-[#1B365D] focus:border-2"
                  >
                    <option value="">Select a category</option>
                    {Object.entries(groupedCategories).map(([group, cats]) => (
                      <optgroup key={group} label={groupLabels[group] || group}>
                        {cats.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.name}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  {validationErrors.category && (
                    <p className="text-[12px] text-red-600 mt-1">{validationErrors.category}</p>
                  )}
                </div>

                <div>
                  <label className="block text-[13px] font-semibold text-[#4A5568] mb-1.5">
                    Family Member
                  </label>
                  <select
                    value={formData.familyMemberId}
                    onChange={(e) => setFormData({ ...formData, familyMemberId: e.target.value })}
                    className="w-full h-11 px-3 text-[14px] border border-[#E2E8F0] rounded focus:outline-none focus:border-[#1B365D] focus:border-2"
                  >
                    <option value="">None</option>
                    {familyMembers.map((fm) => (
                      <option key={fm.id} value={fm.id}>
                        {fm.name} ({fm.relationship})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-[13px] font-semibold text-[#4A5568] mb-1.5">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full px-3 py-2 text-[14px] border border-[#E2E8F0] rounded focus:outline-none focus:border-[#1B365D] focus:border-2 placeholder:text-[#718096]"
                    rows={3}
                    placeholder="Optional notes about this expense"
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3">
              <Button variant="secondary" size="default" onClick={onCancel}>
                Cancel
              </Button>
              <Button variant="ghost" size="default" disabled={isUploading} onClick={() => onSaveAsDraft(formData, documentIds)}>
                Save as Draft
              </Button>
              <Button variant="primary" size="default" disabled={isUploading} onClick={() => onSave(formData, documentIds)}>
                {isUploading ? 'Uploading...' : 'Save Expense'}
              </Button>
            </div>
          </div>

          {/* Documents Section */}
          <div className="space-y-4">
            <div className="bg-white rounded-lg border border-[#E2E8F0] p-6">
              <h2 className="text-[16px] font-semibold text-[#1B365D] mb-4">Documents</h2>

              <LibraryDocumentUploader
                onUploadComplete={handleUploadComplete}
                autoUpload
                onUploadingChange={setIsUploading}
              />

              {/* Uploaded documents list */}
              {uploadedDocs.length > 0 && (
                <div className="mt-4 space-y-2">
                  <div className="text-[12px] font-semibold text-[#718096] uppercase tracking-wide">
                    {uploadedDocs.length} {uploadedDocs.length === 1 ? 'document' : 'documents'} ready to link
                  </div>
                  {uploadedDocs.map(doc => (
                    <div
                      key={doc.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                        previewDoc?.id === doc.id
                          ? 'border-[#1B365D] bg-[#F7FAFC]'
                          : 'border-[#E2E8F0] bg-white hover:bg-[#F7FAFC]'
                      }`}
                      onClick={() => setPreviewDoc(previewDoc?.id === doc.id ? null : doc)}
                    >
                      {doc.contentType.startsWith('image/')
                        ? <Image size={16} className="text-[#718096]" />
                        : <FileText size={16} className="text-[#718096]" />
                      }
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-medium text-[#1B365D] truncate">{doc.fileName}</div>
                        <div className="text-[11px] text-[#A0AEC0]">
                          {(doc.fileSize / 1024).toFixed(0)} KB
                        </div>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleRemoveFromStaged(doc.id); }}
                        className="text-[#A0AEC0] hover:text-red-500 p-1"
                        title="Don't link to this expense"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Inline Preview */}
            {previewDoc && (
              <div className="bg-white rounded-lg border border-[#E2E8F0] overflow-hidden" style={{ height: '350px' }}>
                <div className="flex items-center justify-between px-4 py-2 border-b border-[#E2E8F0] bg-[#F7FAFC]">
                  <span className="text-[13px] font-medium text-[#1B365D] truncate">{previewDoc.fileName}</span>
                  <button
                    onClick={() => setPreviewDoc(null)}
                    className="text-[#718096] hover:text-[#1B365D] p-1"
                  >
                    <X size={14} />
                  </button>
                </div>
                <div className="h-[calc(100%-37px)]">
                  <DocumentPreview
                    documentId={previewDoc.id}
                    contentType={previewDoc.contentType}
                    fileName={previewDoc.fileName}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
